import VRM from '@motorway/lib-vrm-node';

import { replaceWhitespace, stringNumberToInt } from 'Utilities/formatter';
import { VRM_REGEX } from 'Utilities/helpers';

import { platformPost } from '../services/platformEndpoints';

import { isMobilePhoneValid, isPhoneValid } from './phoneValidators';

export * from './formValidators/getFieldState';
export * from './formValidators/getFieldStateWithSubmissionErrors';
export * from './formValidators/intent';

export const composeValidators = (...validators) => (value) => validators
  .reduce((error, validator) => error || validator(value), undefined);

const simpleMemoize = (fn) => {
  let lastArg;
  let lastResult;
  return (...args) => {
    const [first] = args;
    if (first !== lastArg) {
      lastArg = first;
      lastResult = fn(...args);
    }
    return lastResult;
  };
};

// Validation functions
const emailValidate = (value) => {
  const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/i;
  return re.test(value);
};

const platformEmailValidate = simpleMemoize(async (value, user) => platformPost('/email-check', {
  email: value,
  userId: user ? user.id : null,
}));

export const requiredValidator = (message) => (value) => (value ? undefined : message || 'Required');

export const noBlankValidator = (message) => (value) => (value && value.trim() !== '' ? undefined : message || 'Required');

export const emailValidator = (message) => (value) => (emailValidate(value) ? undefined : message || 'Email invalid');

export const platformEmailValidator = (errorMessage, existMessage, user) => async (val) => {
  let platformResponse = { message: '', valid: true };
  try {
    platformResponse = await platformEmailValidate(val, user);
  } catch (error) {
    window?.Sentry?.captureException(new Error(error));
  }
  const { message, valid } = platformResponse;
  if (message === 'already_exists') {
    return existMessage;
  }
  return valid ? undefined : errorMessage || 'Email invalid';
};

export const phoneNumberValidator = (message, isMobileOnly = false) => async (val) => {
  const errorMessage = message || 'Phone number invalid';
  if (!val || val.match(/\//g)) {
    return errorMessage;
  }

  const phoneChecker = isMobileOnly ? isMobilePhoneValid : isPhoneValid;

  try {
    return phoneChecker(val) ? undefined : errorMessage;
  } catch (error) {
    window?.Sentry?.captureException(new Error(error));
  }

  return undefined;
};

export const simplePostCodeValidator = (message) => (value) => {
  if (!value) {
    return message || 'Postcode invalid';
  }

  const trimmed = value.replace(/\s+/g, '');
  if (trimmed.length < 2 || trimmed.length > 7) {
    return message || 'Postcode invalid';
  }
  if (!/[A-Za-z]/.test(trimmed) || !/\d/.test(trimmed)) {
    return message || 'Postcode invalid';
  }

  return undefined;
};

export const postCodeValidator = (message) => (val) => {
  const trimmedVal = val?.trim();
  const re = /^([Gg][Ii][Rr] 0[Aa]{2})|((([A-Za-z][0-9]{1,2})|(([A-Za-z][A-Ha-hJ-Yj-y][0-9]{1,2})|(([AZa-z][0-9][A-Za-z])|([A-Za-z][A-Ha-hJ-Yj-y][0-9]?[A-Za-z]))))\s?[0-9][A-Za-z]{2})$/i;
  const match = trimmedVal?.match(re);
  if (!match || trimmedVal !== match[0]) {
    return message || 'Postcode invalid';
  }
  return undefined;
};

export const numberValidator = (message) => (val) => {
  const numberRegex = /^\d+$/;
  if (!val || !numberRegex.test(val)) {
    return message || 'Number invalid';
  }
  return undefined;
};

export const numberCommaSeparatedValidator = (message) => (val) => {
  if (!val || Number.isNaN(stringNumberToInt(val))) {
    return message;
  }
  return undefined;
};

export const checkMotMileage = (message, mileageLastMot) => (val) => {
  if (!mileageLastMot || !val) {
    return undefined;
  }

  if (stringNumberToInt(val) < mileageLastMot) {
    return message;
  }

  return undefined;
};

export const checkHighMileage = (message, {
  bypassCheck,
  last_mot_date: lastTestDate,
  mileageLastMot,
  year,
}) => (mileage) => {
  // The keys BE is sending https://github.com/motorway/motorway-platform/blob/master/app/api/general/handlers/vehicleHandlers.js#L43-L47
  if (bypassCheck || !lastTestDate || !mileageLastMot) {
    return undefined;
  }

  // High mileage check
  const val = stringNumberToInt(mileage);

  const maxMilesPerYear = 30000;
  const maxMilesPerDay = maxMilesPerYear / 365;

  const vehicleYear = year;
  const currentYear = (new Date()).getFullYear();
  const diff = currentYear - vehicleYear;

  const checkMileageIsNotTooHigh = (mileDiff, dayDiff) => (mileDiff / dayDiff <= maxMilesPerDay)
    || (dayDiff <= 30 && mileDiff <= 1000);

  const currentTime = (new Date()).getTime();
  const dayMS = (1000 * 60 * 60 * 24);

  if (!lastTestDate) {
    if (diff <= 1) {
      // A year or less difference, try to calculate from beginning of year
      const startTime = (new Date((new Date()).getFullYear() - diff, 0, 0)).getTime();
      const dayDiff = (currentTime - startTime) / dayMS;

      if (!checkMileageIsNotTooHigh(val, dayDiff)) {
        return message;
      }
    } else if (!checkMileageIsNotTooHigh(val, diff)) {
      return message;
    }
  } else {
    // Use lastTestDate, no more than 82 miles per day diff
    const lastTestTime = (new Date(lastTestDate)).getTime();

    const dayDiff = (currentTime - lastTestTime) / dayMS;
    const mileDiff = val - mileageLastMot;

    if (!checkMileageIsNotTooHigh(mileDiff, dayDiff)) {
      return message;
    }
  }

  return undefined;
};

export const unprettyVRM = (vrm) => vrm.replace(/\s/g, ''); // VRM regex won't accept whitespace
export const isValidVRM = (vrm) => VRM_REGEX.test(unprettyVRM(vrm));
export const coerceVRM = (vrm) => VRM.coerce(vrm)?.[0]?.prettyVrm || vrm;
export const stripSpaces = (str) => str.toUpperCase().replace(/[^A-Z0-9\s]/, '');

export const VRMValidator = (message) => (val) => {
  const valid = isValidVRM(val);
  if (!valid) {
    return message || 'VRM invalid';
  }
  return undefined;
};

export const verificationCodeValidator = (message) => (value) => {
  const formattedValue = replaceWhitespace(value);
  if (formattedValue.length !== 6) {
    return message || 'invalid verification code';
  }
  return undefined;
};
